/** Console authentication: `/auth/me` gate, CSRF token wiring, RBAC helpers (Functional Specification §3). */
import { can as canDo, type Action, type Me, type Resource, isGlobalAdmin } from '@uatp/shared';
import { Button, Spinner } from '@uatp/ui';
import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { type ApiError, get, post, setCsrfToken } from '../api/client';
import { disconnectRealtime } from '../api/realtime';
import { queryClient } from '../lib/query';

export const ME_KEY = ['auth', 'me'] as const;

interface AuthValue {
  me: Me;
  isGlobal: boolean;
  can: (resource: Resource, action: Action) => boolean;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth() must be used inside <AuthGate>.');
  return ctx;
}

/** Shorthand for permission checks in components. */
export function useCan(resource: Resource, action: Action): boolean {
  return useAuth().can(resource, action);
}

export async function logout(): Promise<void> {
  try {
    await post('/auth/logout');
  } catch {
    /* already signed out */
  }
  disconnectRealtime();
  queryClient.clear();
  window.location.assign('/login');
}

export function FullPageSpinner({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="grid h-dvh place-items-center bg-bg" role="status" aria-live="polite">
      <div className="flex flex-col items-center gap-3 text-13 text-text-2">
        <Spinner size={22} />
        {label}
      </div>
    </div>
  );
}

/** Renders children only for a signed-in admin; otherwise redirects to /login?returnTo=… */
export function AuthGate({ children }: { children: ReactNode }) {
  const location = useLocation();
  const q = useQuery<Me, ApiError>({
    queryKey: ME_KEY,
    queryFn: () => get<Me>('/auth/me'),
    retry: false,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (q.data) setCsrfToken(q.data.csrfToken);
  }, [q.data]);

  const value = useMemo<AuthValue | null>(() => {
    if (!q.data) return null;
    const me = q.data;
    return { me, isGlobal: isGlobalAdmin(me.role), can: (r, a) => canDo(me.role, r, a), logout };
  }, [q.data]);

  if (q.isPending) return <FullPageSpinner />;
  if (q.error) {
    if (q.error.status === 401) {
      const returnTo = `${location.pathname}${location.search}`;
      return <Navigate to={returnTo === '/' ? '/login' : `/login?returnTo=${encodeURIComponent(returnTo)}`} replace />;
    }
    return (
      <div className="grid h-dvh place-items-center bg-bg p-6">
        <div className="flex max-w-sm flex-col items-center gap-3 text-center">
          <h1 className="text-h2">Can’t reach the console</h1>
          <p className="text-13 text-text-2">{q.error.message}</p>
          <Button onClick={() => void q.refetch()}>Try again</Button>
        </div>
      </div>
    );
  }
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Route guard for pages that need a permission (renders a 403 panel instead of the page). */
export function RequirePermission({ resource, action, children }: { resource: Resource; action: Action; children: ReactNode }) {
  const allowed = useCan(resource, action);
  if (!allowed) {
    return (
      <div className="mx-auto mt-16 flex max-w-md flex-col items-center gap-2 text-center">
        <h1 className="text-h2">You don’t have access to this page</h1>
        <p className="text-13 text-text-2">Ask your global admin to change your role in Users &amp; access.</p>
      </div>
    );
  }
  return <>{children}</>;
}
